import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {SchemaTag} from 'folder-items/FileConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import useSelectorsById, {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {MaximumTableHeight} from 'notebook/tables/constants/TableLayoutConstants';
import {UpdateServerProcessOutput} from 'processes/dao/ProcessManagementDAO';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import {SubmitOutputsConfirmationQuery} from 'processes/tables/__generated/SubmitOutputsConfirmationQuery';
import {createOutputTableWithOptionalProcess} from 'processes/tables/createOutputTable';
import createReadOnlyProcessTable from 'processes/tables/createReadOnlyProcessTable';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {CenteredSpinner} from 'ui-library/Spinner';
import omitKey from 'util/omitKey';

type ProcessOutput = ExtractByTypename<
  SubmitOutputsConfirmationQuery['processOutputs'][number],
  'ProcessOutputNode'
>;

interface UpdateMappedOutputsConfirmationPaneProps {
  outputsAlreadyMappedWithUpdatedFields: Array<UpdateServerProcessOutput>;
  outputs: Array<ProcessOutput>;
  onChangeAreQueriesCompleted: (isSubmitEnabled: boolean) => void;
}

function buildOutputTable(
  outputsBeingUpdated: Array<UpdateServerProcessOutput>,
  outputs: Array<ProcessOutput>,
  selectorsById: SchemaFieldSelectorsDict
): ProcessOutputStructuredTable {
  const processTaskSchema = outputs[0].task.schema;
  const outputsById = outputs.reduce(
    (acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    },
    {} as {[id: string]: ProcessOutput}
  );

  const outputsForTable = outputsBeingUpdated.map((output) => ({
    id: output.id,
    humanId: outputsById[output.id].humanId,
    task: {
      id: outputsById[output.id].task.id,
      humanId: outputsById[output.id].task.humanId,
    },
    fields: outputsById[output.id].fields.map((field: SchemaTag) => ({
      // don't reuse the resolved value from the original (stale) field,
      // let the output table resolve the typed value itself.
      ...omitKey(field, 'value'),
      ...output.fields?.[field.name],
    })),
    archiveRecord: null,
  }));

  const outputTable = createOutputTableWithOptionalProcess({
    outputs: outputsForTable,
    processTaskSchema: {
      ...processTaskSchema,
      outputSchema: processTaskSchema.outputSchema!,
    },
    metadata: {tableLocation: ProcessTableLocation.STATUS_CONFIRMATION_MODAL},
    selectorsById,
  });

  return createReadOnlyProcessTable<ProcessOutputStructuredTable>(outputTable, selectorsById);
}

function UpdateMappedOutputsWithMessage({
  numOutputsAlreadyMappedWithUpdatedFields,
}: {
  numOutputsAlreadyMappedWithUpdatedFields: number;
}): JSX.Element {
  const numOutputsString = `${numOutputsAlreadyMappedWithUpdatedFields} ${pluralize(
    numOutputsAlreadyMappedWithUpdatedFields,
    'output'
  )}`;
  return (
    <div>
      You are about to submit updates to {numOutputsString} that{' '}
      {pluralize(numOutputsAlreadyMappedWithUpdatedFields, 'has', 'have')} already been mapped. These
      new&nbsp; updates <strong>won't</strong> be mapped to the next tasks or outputs in the flowchart.
    </div>
  );
}

function UpdateMappedOutputsWithTable(props: UpdateMappedOutputsConfirmationPaneProps): JSX.Element {
  const selectorsById = useSelectorsById({includeArchivedSelectors: true});
  const {onChangeAreQueriesCompleted} = props;

  useEffect(() => {
    onChangeAreQueriesCompleted(selectorsById !== AsyncStatus.LOADING && selectorsById !== AsyncStatus.ERROR);
  }, [selectorsById, onChangeAreQueriesCompleted]);

  if (selectorsById === AsyncStatus.LOADING) {
    return <CenteredSpinner />;
  } else if (selectorsById === AsyncStatus.ERROR || selectorsById === AsyncStatus.NOT_FETCHED) {
    return <div>{GENERIC_ERROR_MESSAGE}</div>;
  }

  const readOnlyOutputTable = buildOutputTable(
    props.outputsAlreadyMappedWithUpdatedFields,
    props.outputs,
    selectorsById
  );

  return (
    <>
      <div>
        The following changes are being made to outputs that have already been mapped. These changes will not
        pass on to the corresponding tasks or outputs in the next step.
      </div>
      <div className={css(styles.tableWrapper)}>
        <StructuredTableOutsideNotebook
          structuredTable={readOnlyOutputTable}
          cid={readOnlyOutputTable.cid}
          updateHandler={noop}
          locked={true}
          hasPrimarySamples={false}
          hideLeftHeaderControls={true}
          enableFormulas={true}
          maximumHeight={MaximumTableHeight.AUTO_FIXED}
        />
      </div>
    </>
  );
}

export function usesPaneWithoutTableForOutputUpdate({
  outputsAlreadyMappedWithUpdatedFields,
}: {
  outputsAlreadyMappedWithUpdatedFields: Array<UpdateServerProcessOutput>;
}): boolean {
  return (
    outputsAlreadyMappedWithUpdatedFields.length >
    BenchlingConfig.WORKFLOWS_MAX_ROWS_FOR_LEGACY_CONFIRMATION_MODALS
  );
}

export default function UpdateMappedOutputsConfirmationPane(
  props: UpdateMappedOutputsConfirmationPaneProps
): JSX.Element {
  return usesPaneWithoutTableForOutputUpdate(props) ? (
    <UpdateMappedOutputsWithMessage
      numOutputsAlreadyMappedWithUpdatedFields={props.outputsAlreadyMappedWithUpdatedFields.length}
    />
  ) : (
    <UpdateMappedOutputsWithTable {...props} />
  );
}

const styles = StyleSheet.create({
  tableWrapper: {
    marginTop: '8px',
    marginRight: '16px',
  },
});
